import { useState } from "react";
import { api, ApiError } from "@/lib/apiClient";
import type { CurrentUser } from "@/types/user";
import { formatTime } from "@/lib/date";
import { useToast } from "@/hooks/useToast";

interface LinkCodeResponse {
  code: string;
  expiresAt: string;
  lineOfficialUrl: string;
}

export function SettingsPage({
  currentUser,
  onUserChanged,
}: {
  currentUser: CurrentUser;
  onUserChanged: () => void;
}) {
  const [issuing, setIssuing] = useState(false);
  const [linkCode, setLinkCode] = useState<LinkCodeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [unlinking, setUnlinking] = useState(false);
  const notify = useToast();

  const issueCode = async () => {
    setIssuing(true);
    setError(null);
    try {
      const result = await api.post<LinkCodeResponse>("/line/link-code");
      setLinkCode(result);
      notify("連携コードを発行しました。");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "連携コードの発行に失敗しました。");
    } finally {
      setIssuing(false);
    }
  };

  const unlink = async () => {
    if (!confirm("LINE連携を解除しますか？")) return;
    setUnlinking(true);
    try {
      await api.delete("/line/link");
      notify("LINE連携を解除しました。");
      onUserChanged();
    } finally {
      setUnlinking(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout");
    window.location.reload();
  };

  return (
    <div>
      <h1 className="page-title">設定</h1>
      <p className="page-subtitle">アカウントとLINE通知の設定を行います。</p>

      <div className="card card-padded" style={{ maxWidth: 480, marginBottom: 20 }}>
        <h2 className="section-title">アカウント情報</h2>
        <p style={{ fontSize: 14, margin: "4px 0" }}>氏名：{currentUser.displayName}</p>
        <p style={{ fontSize: 14, margin: "4px 0" }}>メールアドレス：{currentUser.email}</p>
        <p style={{ fontSize: 14, margin: "4px 0 16px" }}>
          権限：{currentUser.role === "admin" ? "管理者" : currentUser.role === "manager" ? "承認者" : "利用者"}
        </p>
        <button className="btn btn-secondary btn-block" onClick={logout}>
          ログアウト
        </button>
      </div>

      <div className="card card-padded" style={{ maxWidth: 480 }}>
        <h2 className="section-title">LINE通知設定</h2>
        <p style={{ fontSize: 14, marginBottom: 16 }}>
          現在：{currentUser.lineLinked ? "連携済み" : "未連携"}
        </p>

        {!currentUser.lineLinked && (
          <>
            <a
              className="btn btn-primary btn-block"
              style={{ marginBottom: 10, textDecoration: "none" }}
              href={currentUser.lineOfficialUrl}
              target="_blank"
              rel="noreferrer"
            >
              LINEを友だち追加
            </a>
            <button className="btn btn-secondary btn-block" onClick={issueCode} disabled={issuing}>
              {issuing ? "発行中..." : "連携コードを発行"}
            </button>

            {linkCode && (
              <div
                style={{
                  marginTop: 16,
                  padding: 16,
                  borderRadius: 12,
                  background: "var(--color-primary-soft)",
                  fontSize: 14,
                }}
              >
                <p style={{ margin: "0 0 8px", fontWeight: 700, fontSize: 20, letterSpacing: 2 }}>
                  連携 {linkCode.code}
                </p>
                <p style={{ margin: 0 }}>
                  上記のメッセージを公式LINEへ送信してください。有効期限：{formatTime(linkCode.expiresAt)} まで
                </p>
              </div>
            )}
          </>
        )}

        {currentUser.lineLinked && (
          <button className="btn btn-danger btn-block" onClick={unlink} disabled={unlinking}>
            {unlinking ? "解除中..." : "LINE連携を解除"}
          </button>
        )}

        {error && <p className="error-text">{error}</p>}
      </div>
    </div>
  );
}
